import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { locations, primaryLocation } from "@/lib/locations-data";
import { siteConfig } from "@/lib/site-config";

/** Brand blue / brand yellow, mirroring the CSS custom properties. */
const BRAND_BLUE = "hsl(202, 68%, 30%)";
const BRAND_YELLOW = "hsl(41, 92%, 53%)";
/** 75-mile service radius in meters. */
const SERVICE_RADIUS_M = 120700;

const LocationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const [activeSlug, setActiveSlug] = useState<string>(primaryLocation.slug);

  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      // Load Leaflet JS + its CSS on demand so neither weighs on the initial,
      // render-blocking bundle (the map is below the fold and IO-gated).
      const [L] = await Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]);
      if (cancelled) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
      }).setView([primaryLocation.lat, primaryLocation.lng], 8);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // Service radius circle
      L.circle([primaryLocation.lat, primaryLocation.lng], {
        radius: SERVICE_RADIUS_M,
        color: BRAND_BLUE,
        fillColor: BRAND_BLUE,
        fillOpacity: 0.07,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: `<div style="background:${BRAND_YELLOW};width:36px;height:36px;border-radius:50%;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#0d1b24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
        </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      const cityIcon = L.divIcon({
        className: "custom-marker-city",
        html: `<div style="background:${BRAND_BLUE};width:20px;height:20px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      locations.forEach((loc) => {
        const icon = loc.isPrimary ? primaryIcon : cityIcon;
        const marker = L.marker([loc.lat, loc.lng], {
          icon,
          // Accessible name for the keyboard-focusable marker (role="button")
          title: `${loc.name}, ${loc.state}`,
          alt: `${loc.name}, ${loc.state}`,
        })
          .addTo(map)
          .bindPopup(
            `<div style="padding:8px;font-family:sans-serif;min-width:180px;">
              <strong style="font-size:14px;color:${BRAND_BLUE};">${loc.name}, ${loc.state}</strong>
              <p style="color:#666;font-size:12px;margin:4px 0 6px;">${loc.region}${loc.isPrimary ? " · HQ" : ""}</p>
              <a href="/locations/${loc.slug}" style="color:${BRAND_BLUE};font-weight:600;font-size:12px;text-decoration:none;">View location →</a>
            </div>`
          );
        markersRef.current[loc.slug] = marker;
      });
    };

    // Defer Leaflet import + OSM tile loading until the map scrolls into view,
    // so it never competes with the initial page load for bandwidth / main thread.
    const el = mapRef.current;
    let observer: IntersectionObserver | null = null;
    if (el && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            observer?.disconnect();
            observer = null;
            initMap();
          }
        },
        { rootMargin: "300px" }
      );
      observer.observe(el);
    } else {
      initMap();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleLocationClick = (slug: string, lat: number, lng: number) => {
    setActiveSlug(slug);
    if (mapInstanceRef.current) {
      const zoom = slug === primaryLocation.slug ? 10 : 12;
      mapInstanceRef.current.flyTo([lat, lng], zoom, { duration: 1 });
      setTimeout(() => {
        markersRef.current[slug]?.openPopup();
      }, 800);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
      <div className="bg-primary px-5 py-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary-foreground" />
        <span className="font-heading font-bold text-primary-foreground text-sm tracking-wide">
          Service Area — {siteConfig.address.serviceRadius} from {siteConfig.address.city},{" "}
          {siteConfig.address.state}
        </span>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-[35%] lg:border-r border-border overflow-y-auto max-h-[350px] lg:max-h-[520px] scrollbar-hide">
          {locations.map((loc) => (
            <div
              key={loc.slug}
              className={`flex items-center justify-between gap-2 pr-4 border-b border-border transition-all ${
                activeSlug === loc.slug
                  ? "bg-primary/10 border-l-4 border-l-primary"
                  : "hover:bg-muted/50 border-l-4 border-l-transparent"
              }`}
            >
              <button
                onClick={() => handleLocationClick(loc.slug, loc.lat, loc.lng)}
                className="flex-1 text-left px-5 py-3 flex items-center gap-2.5 min-w-0"
              >
                {loc.isPrimary ? (
                  <span className="h-3 w-3 rounded-full bg-secondary shrink-0 ring-2 ring-secondary/30" />
                ) : (
                  <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
                )}
                <span className="min-w-0">
                  <span
                    className={`block font-heading font-bold text-sm tracking-tight truncate ${
                      activeSlug === loc.slug ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {loc.name}, {loc.state}
                  </span>
                  <span className="block text-muted-foreground text-[11px] truncate">
                    {loc.region}
                    {loc.isPrimary ? " · HQ" : ""}
                  </span>
                </span>
              </button>
              <Link
                to={`/locations/${loc.slug}`}
                className="text-xs font-semibold text-primary hover:text-primary/80 whitespace-nowrap shrink-0"
              >
                View →
              </Link>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-[65%] h-[400px] lg:h-[520px] relative" style={{ zIndex: 0 }}>
          <div ref={mapRef} className="w-full h-full" style={{ zIndex: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default LocationMap;
